import { useEffect, useState } from 'react';

import { api } from '../api/endpoints';
import type { EventDetail } from '../api/types';
import { EventDiscussion } from '../components/EventDiscussion';
import { PersonCardView } from '../components/PersonCardView';
import { formatEventDate, formatGoing, formatIdr, formatSpotsLeft } from '../format';

interface Props {
  eventId: number;
  onBack: () => void;
  onOpenPerson: (id: number) => void;
}

export function EventDetailPage({ eventId, onBack, onOpenPerson }: Props) {
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [balance, setBalance] = useState(0);
  const [useBonuses, setUseBonuses] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .event(eventId)
      .then(setEvent)
      .catch(() => setError('Не удалось загрузить мероприятие'));
    // Баланс нужен здесь же: предлагать списание, не зная суммы,
    // значит заставлять человека уходить проверять её в другой раздел.
    api
      .bonuses()
      .then((data) => setBalance(data.balance_idr))
      .catch(() => setBalance(0));
  }, [eventId]);

  async function toggle() {
    if (!event || busy) return;
    setBusy(true);
    setError(null);
    try {
      const going = event.my_status && event.my_status !== 'cancelled';
      if (going) {
        await api.leave(event.id);
      } else {
        await api.join(event.id, useBonuses);
      }
      // Перезагружаем целиком: изменился и список «кто идёт», и счётчик.
      setEvent(await api.event(event.id));
    } catch {
      setError('Не получилось. Попробуйте ещё раз.');
    } finally {
      setBusy(false);
    }
  }

  if (error && !event) {
    return (
      <div className="screen">
        <button className="back" onClick={onBack}>← Назад</button>
        <p className="muted">{error}</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="screen">
        <button className="back" onClick={onBack}>← Назад</button>
        <div className="skeleton" />
      </div>
    );
  }

  const going = Boolean(event.my_status && event.my_status !== 'cancelled');
  const paid = event.my_status === 'paid' || event.my_status === 'attended';
  const isPast = new Date(event.starts_at) < new Date();

  return (
    <div className="screen screen--detail">
      <button className="back" onClick={onBack}>← Назад</button>

      {event.poster_url && (
        <img className="detail__poster" src={event.poster_url} alt="" />
      )}

      <div className="card__meta">
        <span className="chip">{event.category.name}</span>
        {event.visibility === 'club' && <span className="chip chip--club">Клуб</span>}
      </div>

      <h1 className="detail__title">{event.title}</h1>

      <dl className="facts">
        <div className="fact">
          <dt>Когда</dt>
          <dd>{formatEventDate(event.starts_at)}</dd>
        </div>
        {event.location && (
          <div className="fact">
            <dt>Где</dt>
            <dd>{event.location}</dd>
          </div>
        )}
        <div className="fact">
          <dt>Стоимость</dt>
          <dd>{formatIdr(event.price_idr)}</dd>
        </div>
        {event.spots_left !== null && (
          <div className="fact">
            <dt>Места</dt>
            <dd>{formatSpotsLeft(event.spots_left)}</dd>
          </div>
        )}
      </dl>

      {event.description && <p className="detail__text">{event.description}</p>}

      {event.programme && (
        <section className="block">
          <h2 className="block__title">Программа</h2>
          <p className="detail__text">{event.programme}</p>
        </section>
      )}

      {/* Ради этого блока и строится продукт: человек видит, с кем
          ему интересно познакомиться, ещё до мероприятия. */}
      <section className="block">
        <h2 className="block__title">
          Кто идёт{event.going_count > 0 && ` — ${formatGoing(event.going_count)}`}
        </h2>
        {event.going.length === 0 ? (
          <p className="muted">Пока никто не записался. Будьте первым.</p>
        ) : (
          <div className="people">
            {event.going.map((person) => (
              <PersonCardView
                key={person.user_id}
                person={person}
                onOpen={onOpenPerson}
              />
            ))}
          </div>
        )}
      </section>

      {event.organizer && (
        <section className="block">
          <h2 className="block__title">Организатор</h2>
          <PersonCardView person={event.organizer} onOpen={onOpenPerson} />
        </section>
      )}

      <EventDiscussion eventId={event.id} joined={going} isPast={isPast} />

      {error && <p className="error">{error}</p>}

      <div className="sticky-action">
        {/* Бонусами можно закрыть все 100% — потолка нет, так в ТЗ.
            Поэтому и предлагаем до записи, а не после оплаты. */}
        {!going && !paid && event.price_idr > 0 && balance > 0 && (
          <label className="checkline checkline--action">
            <input
              type="checkbox"
              checked={useBonuses}
              onChange={(e) => setUseBonuses(e.target.checked)}
            />
            <span>
              Списать бонусы — доступно {balance.toLocaleString('ru-RU')} IDR
              {balance >= event.price_idr
                ? ' (хватает полностью)'
                : `, доплата ${(event.price_idr - balance).toLocaleString('ru-RU')}`}
            </span>
          </label>
        )}

        {paid ? (
          <div className="action-note">
            Участие оплачено. Отмену оформляет организатор.
          </div>
        ) : isPast ? (
          <div className="action-note">Мероприятие уже прошло.</div>
        ) : (
          <button
            className={going ? 'btn btn--secondary' : 'btn'}
            onClick={toggle}
            disabled={busy}
          >
            {busy ? '…' : going ? 'Не пойду' : 'Участвовать'}
          </button>
        )}
      </div>
    </div>
  );
}
